import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// Koala — a storybook koala hugging a short eucalyptus stump, arms wrapped
// around the trunk. Chunky toy proportions: a huge round head with big fluffy
// ears and a big oval nose on a small plump body. Built to animate: the head
// and each ear are named so the "sleepy" loop can tilt the head over with a
// drowsy ear wiggle while the idle keeps the little chest breathing.
//
// Frame: faces +Z, sits on the floor (y = 0), the stump behind it. About
// 0.6 m tall.
//
// Rig layout (flat, no hierarchy — each named node moves about its own pivot):
//   - "body"  : torso, belly, legs and hugging arms. Breathes.
//   - "head"  : the whole face (nose, eyes, cheeks). Pivot at the neck.
//   - "ear-l" / "ear-r" : the big fluffy ears. Pivot where each meets the
//                head, so wiggles read as flops, not orbits.
// The stump is static (unnamed) and merges into the base mesh.

const FUR = "#a89b90";         // warm grey fur
const FUR_SHADE = "#8b7d72";   // shaded fur for limbs and ear backs
const CREAM = "#f6efe3";       // belly, chin and inner-ear fluff
const NOSE = "#4a3d42";        // the big oval nose — soft charcoal, not black
const EYE_WHITE = "#faf6ec";   // eye whites
const PUPIL = "#33282e";       // pupils
const BARK = "#8a674a";        // eucalyptus stump bark
const BARK_SHADE = "#6f5138";  // bark shading / root flare
const WOOD = "#cfa878";        // cut top of the stump
const LEAF = "#7fae7a";        // a eucalyptus sprig on the stump

// The pivots the animations rotate the named nodes about.
const BODY_PIVOT: [number, number, number] = [0, 0.20, 0.06];
const NECK_PIVOT: [number, number, number] = [0, 0.36, 0.05];
const EAR_L_PIVOT: [number, number, number] = [-0.13, 0.53, 0.03];
const EAR_R_PIVOT: [number, number, number] = [0.13, 0.53, 0.03];

// One big friendly eye: white ball, big pupil slightly forward, catch-light.
function eye(x: number): Part[] {
  return [
    { geometry: new THREE.SphereGeometry(0.03, 10, 8), color: EYE_WHITE,
      position: [x, 0.50, 0.18], name: "head" },
    { geometry: new THREE.SphereGeometry(0.019, 8, 6), color: PUPIL,
      position: [x * 0.94, 0.498, 0.2], name: "head" },
    { geometry: new THREE.SphereGeometry(0.0065, 6, 5), color: EYE_WHITE,
      position: [x * 0.94 + 0.007, 0.507, 0.215], name: "head" },
  ];
}

// One big fluffy ear: a round outer puff plus a cream inner puff facing front.
function ear(name: string, x: number): Part[] {
  return [
    { geometry: new THREE.SphereGeometry(0.088, 10, 8), color: FUR,
      position: [x * 1.19, 0.555, 0.02], scale: [1.0, 1.0, 0.65], name },
    { geometry: new THREE.SphereGeometry(0.056, 8, 6), color: CREAM,
      position: [x * 1.23, 0.552, 0.062], scale: [0.95, 0.95, 0.5], name },
  ];
}

// One hugging arm: an upper arm reaching back around the trunk plus a paw
// pressed against the bark.
function hugArm(x: number): Part[] {
  return [
    { geometry: new THREE.CylinderGeometry(0.042, 0.048, 0.2, 8), color: FUR_SHADE,
      position: [x, 0.315, -0.03], rotation: [1.25, 0, -Math.sign(x) * 0.35], name: "body" },
    { geometry: new THREE.SphereGeometry(0.045, 8, 6), color: FUR_SHADE,
      position: [x * 0.62, 0.345, -0.135], name: "body" },
  ];
}

export const koala = defineModel({
  id: "koala",
  batchId: "mess",
  name: "Koala",
  tags: ["mess", "cute", "animated", "creature", "animal", "koala", "marsupial", "eucalyptus", "mascot"],
  summary: "A big-eared koala hugging a short eucalyptus stump, drifting off to sleep.",
  description:
    "A storybook koala clinging to a short eucalyptus stump: a huge round " +
    "head with big fluffy cream-lined ears and a big oval nose, a small plump " +
    "body with arms wrapped back around the trunk. The head and each ear are " +
    "separately posable; the sleepy loop tilts the head over with a drowsy " +
    "ear wiggle while the idle keeps the chest breathing.",
  author: "models",
  footprint: 0.35,
  tiles: { w: 1, h: 1 },
  parts(): Part[] {
    return [
      // ── the eucalyptus stump (static) ──────────────────────────────────
      { geometry: new THREE.CylinderGeometry(0.08, 0.095, 0.5, 9), color: BARK,
        position: [0, 0.25, -0.13] },
      // Cut top.
      { geometry: new THREE.CylinderGeometry(0.072, 0.072, 0.018, 9), color: WOOD,
        position: [0, 0.505, -0.13] },
      // Root flare at the base.
      { geometry: new THREE.CylinderGeometry(0.105, 0.13, 0.06, 9), color: BARK_SHADE,
        position: [0, 0.03, -0.13] },
      // A stub branch with a little leaf sprig.
      { geometry: new THREE.CylinderGeometry(0.02, 0.025, 0.1, 6), color: BARK_SHADE,
        position: [0.09, 0.43, -0.16], rotation: [0, 0, -0.9] },
      { geometry: new THREE.SphereGeometry(0.05, 7, 6), color: LEAF,
        position: [0.15, 0.46, -0.16], scale: [1.2, 0.8, 1.0] },
      // ── body (hugging the stump from the front) ────────────────────────
      { geometry: new THREE.SphereGeometry(0.17, 12, 10), color: FUR,
        position: [0, 0.20, 0.06], scale: [1.0, 1.15, 0.95], name: "body" },
      // Cream belly.
      { geometry: new THREE.SphereGeometry(0.115, 10, 8), color: CREAM,
        position: [0, 0.18, 0.14], scale: [1.0, 1.1, 0.7], name: "body" },
      // Stubby legs folded at the stump's base.
      { geometry: new THREE.SphereGeometry(0.075, 8, 6), color: FUR_SHADE,
        position: [-0.125, 0.07, 0.1], scale: [0.9, 0.8, 1.25], name: "body" },
      { geometry: new THREE.SphereGeometry(0.075, 8, 6), color: FUR_SHADE,
        position: [0.125, 0.07, 0.1], scale: [0.9, 0.8, 1.25], name: "body" },
      ...hugArm(-0.145),
      ...hugArm(0.145),
      // ── head ───────────────────────────────────────────────────────────
      // The huge round head — most of the koala.
      { geometry: new THREE.SphereGeometry(0.16, 14, 12), color: FUR,
        position: [0, 0.47, 0.06], scale: [1.1, 1.0, 0.95], name: "head" },
      // Fluffy cheeks.
      { geometry: new THREE.SphereGeometry(0.06, 8, 6), color: CREAM,
        position: [-0.095, 0.425, 0.145], scale: [1.0, 0.85, 0.7], name: "head" },
      { geometry: new THREE.SphereGeometry(0.06, 8, 6), color: CREAM,
        position: [0.095, 0.425, 0.145], scale: [1.0, 0.85, 0.7], name: "head" },
      // The big oval nose — the koala's signature.
      { geometry: new THREE.SphereGeometry(0.048, 10, 8), color: NOSE,
        position: [0, 0.455, 0.195], scale: [0.8, 1.35, 0.6], name: "head" },
      ...eye(-0.068),
      ...eye(0.068),
      // ── the big fluffy ears ────────────────────────────────────────────
      ...ear("ear-l", -0.13),
      ...ear("ear-r", 0.13),
    ];
  },
  animations: [
    // 1. Idle — the little chest breathes and the head bobs a hair; the ears
    //    give the occasional soft twitch.
    {
      id: "idle",
      label: "Idle breathing",
      duration: 3.6,
      loop: true,
      tracks: [
        {
          target: "body", pivot: BODY_PIVOT,
          scale: [
            { t: 0, value: 1, ease: "sine" },
            { t: 1.8, value: 1.03, ease: "sine" },
            { t: 3.6, value: 1, ease: "sine" },
          ],
        },
        {
          target: "head", pivot: NECK_PIVOT,
          position: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 1.8, value: [0, 0.01, 0], ease: "sine" },
            { t: 3.6, value: [0, 0, 0], ease: "sine" },
          ],
        },
        {
          target: "ear-l", pivot: EAR_L_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 1.5, value: [0, 0, 0], ease: "sine" },
            { t: 1.7, value: [0, 0, 0.14], ease: "easeOut" },
            { t: 1.9, value: [0, 0, 0], ease: "easeIn" },
            { t: 3.6, value: [0, 0, 0], ease: "sine" },
          ],
        },
        {
          target: "ear-r", pivot: EAR_R_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 3.0, value: [0, 0, 0], ease: "sine" },
            { t: 3.2, value: [0, 0, -0.14], ease: "easeOut" },
            { t: 3.4, value: [0, 0, 0], ease: "easeIn" },
            { t: 3.6, value: [0, 0, 0], ease: "sine" },
          ],
        },
      ],
    },

    // 2. Sleepy — the head slowly tips over to one shoulder and rests there,
    //    ears wiggling drowsily, then rights itself with a small startle.
    {
      id: "sleepy",
      label: "Sleepy head tilt",
      duration: 5.0,
      loop: true,
      tracks: [
        {
          target: "head", pivot: NECK_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "easeInOut" },
            { t: 1.4, value: [0.1, 0, 0.42], ease: "easeInOut" },
            { t: 3.6, value: [0.14, 0, 0.48], ease: "sine" },
            { t: 4.0, value: [0.14, 0, 0.48], ease: "easeInOut" },
            { t: 4.4, value: [-0.06, 0, -0.05], ease: "easeOut" },
            { t: 5.0, value: [0, 0, 0], ease: "easeInOut" },
          ],
        },
        {
          target: "ear-l", pivot: EAR_L_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "easeInOut" },
            { t: 1.4, value: [0, 0, 0.42], ease: "easeInOut" },
            { t: 2.2, value: [0, 0, 0.62], ease: "sine" },
            { t: 3.0, value: [0, 0, 0.42], ease: "sine" },
            { t: 3.8, value: [0, 0, 0.55], ease: "sine" },
            { t: 4.4, value: [0, 0, -0.05], ease: "easeOut" },
            { t: 5.0, value: [0, 0, 0], ease: "easeInOut" },
          ],
        },
        {
          target: "ear-r", pivot: EAR_R_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "easeInOut" },
            { t: 1.4, value: [0, 0, 0.42], ease: "easeInOut" },
            { t: 2.6, value: [0, 0, 0.28], ease: "sine" },
            { t: 3.4, value: [0, 0, 0.5], ease: "sine" },
            { t: 4.4, value: [0, 0, 0.05], ease: "easeOut" },
            { t: 5.0, value: [0, 0, 0], ease: "easeInOut" },
          ],
        },
        {
          // The body slumps a touch with the tilt.
          target: "body", pivot: BODY_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "easeInOut" },
            { t: 1.4, value: [0, 0, 0.06], ease: "easeInOut" },
            { t: 4.0, value: [0, 0, 0.06], ease: "easeInOut" },
            { t: 5.0, value: [0, 0, 0], ease: "easeInOut" },
          ],
        },
      ],
    },
  ],
});
